import React, { useState, useEffect } from 'react';
import { TopSection } from './About';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import JoinIcon from '../assets/af1.png';
import ReferIcon from '../assets/af2.png';
import EarnIcon from '../assets/af3.png';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Styled Components (keeping all your existing styles)
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const HeaderIcons = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto 60px;
  padding: 20px;

  @media (max-width: 768px) {
    gap: 20px;
    padding: 10px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 150px;

  @media (max-width: 768px) {
    max-width: 100px;
  }
`;

const IconImage = styled.img`
  width: 300px;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 200px;
  }
`;

const MainHeading = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
  font-family: 'Outfit', sans-serif;
  text-align: left;
  margin-left: auto;
  margin-right: auto;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const InfoCard = styled(motion.div)`
  background-color: #ffffff;
  border-radius: 50px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 2px dashed red;
  max-width: 500px;
`;

const CardTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
`;

const CardContent = styled.div`

  p {
    margin-bottom: 10px;
    color: #444;
  font-family: 'Outfit', sans-serif;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Highlight = styled.span`
  font-weight: 600;
  color: #444;
`;

const RegisterButtonWrapper = styled.div`
  margin-top: 20px;
`;

// Animation Variant
const cardVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6 },
  }),
};

const Affiliate = () => {
  const [affiliateData, setAffiliateData] = useState({
    mainHeading: "Love Kiddtopia? Now turn your network into a revenue stream by helping us grow across India!",
    cards: []
  });
  const [loading, setLoading] = useState(true);

  const fetchAffiliateData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/affiliate`);
      setAffiliateData(response.data);
    } catch (error) {
      console.error('Error fetching affiliate data:', error);
      // Keep default data if API fails
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const formatContent = (content) => {
    if (!content) return <p></p>;
    
    return content.split('\n').map((line, index) => {
      if (line.trim() === '') return <br key={index} />;
      
      // Check if line contains highlighted text patterns
      if (line.includes('₹') || line.includes('Franchise Value') || 
          line.includes('Sign Up') || line.includes('Refer') || 
          line.includes('Close the Deal') || line.includes('2% to 2.5%')) {
        // Split by common patterns and highlight them
        const parts = line.split(/(\d+%?\s*to\s*\d+\.?\d*%?|₹[\d\.]+ - ₹[\d\.]+ Lakhs|Franchise Value ₹[\d\.]+ Cr:|Sign Up|Refer|Close the Deal)/);
        return (
          <p key={index}>
            {parts.map((part, partIndex) => {
              if (part.match(/(\d+%?\s*to\s*\d+\.?\d*%?|₹[\d\.]+ - ₹[\d\.]+ Lakhs|Franchise Value ₹[\d\.]+ Cr:|Sign Up|Refer|Close the Deal)/)) {
                return <Highlight key={partIndex}>{part}</Highlight>;
              }
              return part;
            })}
          </p>
        );
      }
      
      return <p key={index}>{line}</p>;
    });
  };

  if (loading) {
    return (
      <>
        <TopSection heading={'Affiliate Program'} />
        <Container>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            Loading...
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <TopSection heading={'Affiliate Program'} />

      <Container>
        <HeaderSection>
          <HeaderIcons>
            <IconWrapper>
              <IconImage src={JoinIcon} alt="Join Icon" />
            </IconWrapper>
            <IconWrapper>
              <IconImage src={ReferIcon} alt="Refer Icon" />
            </IconWrapper>
            <IconWrapper>
              <IconImage src={EarnIcon} alt="Earn Icon" />
            </IconWrapper>
          </HeaderIcons>

          <MainHeading>
            {affiliateData.mainHeading}
          </MainHeading>
        </HeaderSection>

        <InfoGrid>
          {affiliateData.cards.map((item, index) => (
            <InfoCard
              key={index}
              custom={index}
              variants={cardVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <CardTitle>{item.title}</CardTitle>
              <CardContent>
                {formatContent(item.content)}
                {item.title === 'How It Works' && (
                  <RegisterButtonWrapper>
                    <Link to="/affiliate/form">
                      <button className='btn'>Register Now</button>
                    </Link>
                  </RegisterButtonWrapper>
                )}
              </CardContent>
            </InfoCard>
          ))}
        </InfoGrid>
      </Container>
    </>
  );
};

export default Affiliate;